import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Card, CardBottom, CardContent, CardTop, EqualHeight, Heading, Separator, Text } from 'telia-front-react';
import LanguageStore, { Language } from '../../stores/LanguageStore';
import ReservationStore from '../../stores/ReservationStore';
import { TranslationsContext } from '@teliaee/telia-translate';
import BranchBusinessHours from './BranchBusinessHours';
import BranchLocation from './BranchLocation';
import BranchReservationButton from './BranchReservationButton';
import BranchSpecialInfo from './BranchSpecialInfo';
import AccessibilityInformation from './AccessibilityInformation';
import ArrivalInformationLink from './ArrivalInformationLink';
import { Branch, BranchType, TranslationMap } from '../common/ReservationInterfaces';

interface BranchCardProps {
  branch?: Branch;
  languageStore?: LanguageStore;
  reservationStore?: ReservationStore;
}

@observer
class BranchCard extends React.Component<BranchCardProps> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;
  public render(): JSX.Element {
    const translate = this.context.translate;

    const {
      branch,
      languageStore: { currentLanguage }
    } = this.props;

    return (
      <Card key={branch.branchId} background="neutral-bg-level-2">
        <CardContent>
          <CardTop>
            <EqualHeight group={'branch-location'}>
              <Text>
                <Heading element="h3">{branch.name}</Heading>
                {hasTranslation(branch.streetAddress, currentLanguage) && <BranchLocation branch={branch} />}
                {!!branch.accessibilityInformation?.length && <AccessibilityInformation branch={branch} />}
                {!!branch.accessibilityInformation?.length && branch.locationDescriptionLong.et && <ArrivalInformationLink branch={branch} />}
              </Text>
            </EqualHeight>
            <Text>
              <Separator spacing={'xsmall'} className="text__margin-half" />
            </Text>
            <Text>
              <BranchBusinessHours businessHours={branch.businessHours} />
              {this.branchHasChildWithType(BranchType.TECH_STORE) && <p className="text__margin-half">{translate('reservation.technician.notice')}</p>}
              {this.branchHasChildWithType(BranchType.B2B_STORE) && <p className="text__margin-half">{translate('reservation.business.consultant.notice')}</p>}
            </Text>
          </CardTop>
          <CardBottom>
            <Text>
              {(branch.specialHours || hasTranslation(branch.additionalDescription, currentLanguage)) && (
                <BranchSpecialInfo additionalDescription={branch.additionalDescription} specialHours={branch.specialHours} />
              )}
              <BranchReservationButton branch={branch} />
            </Text>
          </CardBottom>
        </CardContent>
      </Card>
    );
  }

  private branchHasChildWithType = (childType: BranchType) => {
    const { reservationStore, branch } = this.props;
    const { children } = reservationStore.findBranchAndChildrenWhenExist(branch);

    return children?.some((e) => e.type === childType);
  };
}

export const hasTranslation = (translations: TranslationMap, language: Language): boolean => {
  return translations && (!!translations[language] || !!translations.et);
};

export default inject('languageStore', 'reservationStore')(BranchCard);
